import React, {Component} from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import Hammer from 'hammerjs';

const propTypes = {
  children: PropTypes.node.isRequired,
  direction: PropTypes.oneOf([
    'none',
    'left',
    'right',
    'up',
    'down',
    'horizontal',
    'vertical',
    'all'
  ]),
  gesture: PropTypes.oneOf(['pinch', 'drag', 'swipe']),
  threshold: PropTypes.number,
  onMove: PropTypes.func,
  onStart: PropTypes.func,
  onStop: PropTypes.func
};

export default class Gesturable extends Component {
  static displayName = 'Gesturable';
  static propTypes = propTypes;

  static defaultProps = {
    direction: 'horizontal',
    onStart() {},
    onMove() {},
    onStop() {}
  };

  componentDidMount() {
    const node = ReactDOM.findDOMNode(this);
    this.initHammer(node);
  }

  componentDidUpdate(prevProps) {
    const newNode = ReactDOM.findDOMNode(this);
    if (this._node !== newNode || this.props.gesture !== prevProps.gesture) {
      this.destroyHammer(prevProps);
      this.initHammer(newNode);
    }
  }

  componentWillUnmount() {
    this.destroyHammer();
  }

  initHammer(node) {
    this._node = node;
    this._hammer = new Hammer.Manager(node, {domEvents: true});
    switch (this.props.gesture) {
      case 'pinch':
        this.addPinchListeners();
        break;
      case 'drag':
        this.addPanListeners();
        break;
      case 'swipe':
        this.addSwipeListeners();
        break;
      default:
        break;
    }
  }

  destroyHammer({gesture} = this.props) {
    if (this._hammer) {
      this._hammer.stop();
      switch (gesture) {
        case 'pinch':
        case 'drag':
          this.removeListeners(gesture);
          break;
        case 'swipe':
          this.removeSwipeListeners();
          break;
        default:
          break;
      }
      this._hammer.destroy();
    }
  }

  handleStart = e => {
    e.preventDefault();
    this.props.onStart(e);
  };

  handleMove = e => {
    e.preventDefault();
    this.props.onMove(e);
  };

  handleStop = e => {
    e.preventDefault();
    this.props.onStop(e);
  };

  events = {
    start: this.handleStart,
    move: this.handleMove,
    end: this.handleStop,
    cancel: this.handleStop
  };

  addListeners(type) {
    Object.keys(this.events).map(event => {
      this._hammer.on(`${type}${event}`, this.events[event]);
    });
  }

  removeListeners(type) {
    Object.keys(this.events).map(event => {
      this._hammer.off(`${type}${event}`, this.events[event]);
    });
  }

  addPinchListeners() {
    this._hammer.add(new Hammer.Pinch({enable: true}));
    this.addListeners('pinch');
  }

  addPanListeners() {
    const config = {
      direction: Hammer.DIRECTION_ALL
    };

    if (this.props.threshold !== undefined) {
      config.threshold = this.props.threshold;
    }

    this._hammer.add(new Hammer.Pan(config));
    this.addListeners('pan');
  }

  addSwipeListeners() {
    const direction = `DIRECTION_${this.props.direction.toUpperCase()}`;
    this._hammer.add(new Hammer.Swipe({direction: Hammer[direction]}));
    this._hammer.on('swipe', this.handleMove);
  }

  removeSwipeListeners() {
    this._hammer.off('swipe', this.handleMove);
  }

  render() {
    return React.Children.only(this.props.children);
  }
}
